import React, { useContext, useMemo, VFC } from 'react';

import { sortBy } from 'lodash';

import ArticleBlock from '/components/ArticleBlock';
import {
  ContentColumn,
  MediaColumn,
  IMAGE_SIZE,
} from '/components/BlockColumns';
import { LAYOUT, useColsCollapsed } from '/components/common';
import { BlockProps, ColumnContainerJson } from '/types';
import { PresentModeContext } from '/utils/contexts';
import { filterKeynoteColumns } from '/utils/functions';
import useAnimation from '/utils/useAnimation';

const BlockFullHeightMedia: VFC<BlockProps> = ({
  animationEnabled,
  animationStep,
  setAnimationComplete,
  blockContent,
  presentOption,
  ...props
}) => {
  const colsCollapsed = useColsCollapsed();
  const layout = blockContent?.columns?.layout;
  const columnData = blockContent?.columns?.list || [];
  const presentMode = useContext(PresentModeContext);

  const columnDataJson = JSON.stringify(columnData);
  const orderedColumns = useMemo(
    () =>
      filterKeynoteColumns(
        // Content first when collapsed
        colsCollapsed
          ? sortBy(JSON.parse(columnDataJson), (column) =>
              column.name === 'media' ? 0 : 1,
            )
          : JSON.parse(columnDataJson),
        presentMode,
        presentOption,
      ),
    [columnDataJson, colsCollapsed, presentMode, presentOption],
  );
  const preChartSteps = animationEnabled ? 1 : 0;
  const columns = useAnimation(
    orderedColumns,
    presentMode,
    animationStep,
    preChartSteps,
    setAnimationComplete,
  );

  let imageSize = layout == LAYOUT.EQUAL ? IMAGE_SIZE.MEDIUM : IMAGE_SIZE.LARGE;
  if (orderedColumns.length < columnData.length) {
    imageSize = IMAGE_SIZE.FULL;
  }

  return (
    <ArticleBlock
      {...props}
      blockContent={blockContent}
      offset={1}
      hideFooter={animationEnabled && !animationStep}
      titleCentered
      rowCenterAlign={layout != LAYOUT.EQUAL}
      columns={columns.map((column: ColumnContainerJson) => (
        <>
          {!column.hidden && column.name === 'media' && (
            <MediaColumn
              elements={column.elements}
              imageSize={imageSize}
              presentOption={presentOption}
              hide={animationEnabled && !animationStep}
            />
          )}

          {!column.hidden && column.name === 'content' && (
            <ContentColumn
              elements={column.elements}
              presentOption={presentOption}
              hide={animationEnabled && !animationStep}
            />
          )}
        </>
      ))}
    />
  );
};

BlockFullHeightMedia.displayName = 'BlockFullHeightMedia';
export default BlockFullHeightMedia;
